<template lang="pug">
v-card(v-if='!chartData' elevation='0')
    v-alert(type='Warning') OOPS... Error loading details
v-card(v-else elevation=0)
    svg.node-occupancy-graph(ref='svg')
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import * as d3 from 'd3';

const TRANSITION_DURATION_MS = 1000;

interface BarGraphDatum {
    timeMin: Date;
    timeMax: Date;
    y: number;
}
interface chartData {
    time: Date;
    value: number;
}

/**
 * Returns true iff two date intervals overlap.
 * @param int1 - A date interval containing two dates
 * @param int2 - A date interval containing two dates
 */
function dateIntervalsOverlap(int1: Date[], int2: Date[]): boolean {
    if (!(int1[0] instanceof Date) || !(int1[1] instanceof Date)) {
        throw Error('Non-dates passed into int1');
    }
    if (!(int2[0] instanceof Date) || !(int2[1] instanceof Date)) {
        throw Error('Non-dates passed into int2');
    }
    return int1[0] < int2[1] && int1[1] > int2[0];
}

/**
 * Returns a new Date with time of `date` shifted by `timeDeltaMS`
 * @param date - The datetime to start from
 * @param timeDeltaMS - The number of milliseconds to shift the `date` by in the newly returned date.
 */
function datePlus(date: Date, timeDeltaMS): Date {
    return new Date(date.getTime() + timeDeltaMS);
}

class BarGrapher {
    constructor(
        private clippedGraphArea,
        private height: number,
    ) {}

    drawBars(
        barGraphData: BarGraphDatum[],
        x: d3.ScaleTime<number, number, never>,
        y: d3.ScaleLinear<number, number, never>,
    ) {
        this.clippedGraphArea
            .append('g')
            .classed('bar-graph-bars-group', true)
            .selectAll('rect')
            .data(barGraphData)
            .join('rect')
            .attr('x', (d) => x(d.timeMin))
            .attr('y', (d) => y(d.y))
            .attr('width', (d) => x(d.timeMax) - x(d.timeMin))
            .attr('height', (d) => this.height - y(d.y));
    }

    updateBars(
        barGraphData: BarGraphDatum[],
        x: d3.ScaleTime<number, number, never>,
        y: d3.ScaleLinear<number, number, never>,
    ) {
        this.clippedGraphArea
            .select('g.bar-graph-bars-group')
            .selectAll('rect')
            .data(barGraphData)
            .join('rect')
            .transition()
            .duration(TRANSITION_DURATION_MS)
            .attr('x', (d) => x(d.timeMin))
            .attr('y', (d) => y(d.y))
            .attr('width', (d) => x(d.timeMax) - x(d.timeMin))
            .attr('height', (d) => this.height - y(d.y));
    }
}

class HorizontalVisualGuides {
    constructor(
        private clippedGraphArea,
        private width: number,
    ) {}

    drawLines(y: d3.ScaleLinear<number, number, never>) {
        this.clippedGraphArea
            .append('g')
            .classed('horizontal-guide-lines', true)
            .selectAll('line')
            .data(y.ticks())
            .join('line')
            .attr('x1', 0)
            .attr('y1', (d) => y(d))
            .attr('x2', this.width)
            .attr('y2', (d) => y(d));
    }

    updateLines(y: d3.ScaleLinear<number, number, never>) {
        this.clippedGraphArea
            .select('g.horizontal-guide-lines')
            .selectAll('line')
            .data(y.ticks())
            .join('line')
            .transition()
            .duration(TRANSITION_DURATION_MS)
            .attr('x1', 0)
            .attr('y1', (d) => y(d))
            .attr('x2', this.width)
            .attr('y2', (d) => y(d));
    }
}

export default defineComponent({
    name: 'BarTimeGraph',
    components: {},
    props: {
        chartData: {
            type: Array as PropType<chartData[]>,
            required: true,
        },
        yAxisLabel: {
            type: String,
            required: true,
        },
        xAxisLabel: {
            type: String,
            required: true,
        },
        graphTitle: {
            type: String,
            default: '',
        },
        graphTitleDataCy: {
            type: String,
            default: undefined,
        },
        graphTimeBufferInMS: {
            type: Number,
            default: undefined,
        },
        totalWidth: {
            type: Number,
            default: 1200,
        },
        totalHeight: {
            type: Number,
            default: 400,
        },
    },
    data() {
        return {};
    },

    computed: {
        chartDataSorted(): chartData[] {
            const data = this.chartData;
            return data.sort((a, b) => d3.ascending(new Date(a.time), new Date(b.time)));
        },
        dataPtTimeMax(): Date {
            return this.chartDataSorted[this.chartDataSorted.length - 1].time;
        },
        dataPtTimeMin(): Date {
            return this.chartDataSorted?.[0].time;
        },
        timeBufferInMS(): number {
            if (this.graphTimeBufferInMS) {
                return (
                    this.graphTimeBufferInMS +
                    (this.dataPtTimeMax.valueOf() - this.dataPtTimeMin.valueOf()) / 20
                );
            } else {
                return 0;
            }
        },

        graphTimeMin(): Date {
            return new Date(this.dataPtTimeMin.valueOf() - this.timeBufferInMS);
        },
        graphTimeMax(): Date {
            return new Date(this.dataPtTimeMax.valueOf() + this.timeBufferInMS);
        },
        dateTimeFormat() {
            if (this.totalWidth > 600 && this.chartDataSorted.length < 15) {
                return d3.timeFormat('%d%H%M %b %y');
            } else {
                return d3.timeFormat('%d %b%y');
            }
        },
    },
    watch: {
        chartData() {
            this.createGraph();
        },
        graphTitle() {
            if (!this.graphTitle) {
                return;
            }
            this.createGraph();
        },
    },
    mounted() {
        if (this.chartDataSorted && this.chartDataSorted.length > 0) {
            this.createGraph();
        }
    },
    methods: {
        createGraph() {
            if (this.chartDataSorted.length < 1) {
                return;
            }
            const totalWidth = this.totalWidth;
            const totalHeight = this.totalHeight;

            const margins = { left: 60, right: 10, top: 40, bottom: 20 };
            const width = totalWidth - margins.left - margins.right;
            const height = totalHeight - margins.top - margins.bottom;

            const svgElement = d3.select(this.$refs.svg);

            // Delete everything, so that a fresh graph is created each time this method is called.
            // This is sub-optimal (visually) to using transitions, but is a simple global solution
            // that can handle any kind of change.  Transitions are still used for zooming.
            svgElement.selectAll('*').remove();

            const svg = svgElement
                .attr('width', totalWidth)
                .attr('height', totalHeight)
                .append('g')
                .attr('transform', `translate(${margins.left} ${margins.top})`);

            // Add a clipPath: everything out of this area won't be drawn.
            svg.append('defs')
                .append('svg:clipPath')
                .attr('id', 'clip')
                .append('svg:rect')
                .attr('width', width)
                .attr('height', height)
                .attr('x', 0)
                .attr('y', 0);

            // Create a clipped area: where both the bar graph and the brush take place
            const clippedGraphArea = svg.append('g').attr('clip-path', 'url(#clip)');

            // Create Title
            svg.append('text')
                .attr('x', 0)
                .attr('y', -margins.top / 2)
                .attr('text-anchor', 'left')
                .text(this.graphTitle)
                .classed('node-occupancy-graph-title', true);

            // x-axis label
            svg.append('text')
                .classed('xy-labels', true)
                .attr('text-anchor', 'end')
                .attr('y', height + 40)
                .attr('x', width)
                .text(this.xAxisLabel);

            // y-axis label
            svg.append('text')
                .classed('xy-labels', true)
                .attr('text-anchor', 'end')
                .attr('y', -10)
                .attr('x', -30)
                .attr('transform', 'rotate(-90, -40, 0)')
                .text(this.yAxisLabel);

            //  __t:  The x and y axes
            const timeMinMax: [Date, Date] = [this.graphTimeMin, this.graphTimeMax];

            // Add X axis (time)
            const x = d3
                .scaleUtc() // I believe we want to use UTC time
                .domain(timeMinMax)
                .nice()
                .range([0, width]);

            const xAxis = svg
                .append('g')
                .attr('transform', `translate(0 ${height})`)
                .call(
                    d3
                        .axisBottom(x)
                        .tickFormat((d) => {
                            // Format the date value using d3.timeFormat()
                            const formatter = d3.timeFormat(this.dateTimeFormat);
                            return formatter(d);
                        })

                        .tickSizeOuter(0),
                )

                .classed('axes', true);
            // xAxis.selectAll('text').attr('transform', 'rotate(-65,20,30)');

            // Add Y axis (value)
            const y = d3
                .scaleLinear()
                .domain(this.getYAxisValuesMinMax(...timeMinMax))
                .nice()
                .range([height, 0]);

            const yAxis = svg
                .append('g')
                .attr('transform', `translate(0 0)`) // Since at left
                .call(d3.axisLeft(y))
                .classed('axes', true);

            const horizontalVisualGuides = new HorizontalVisualGuides(clippedGraphArea, width);
            horizontalVisualGuides.drawLines(y);

            //  __t:  The bar graph
            const barGraphData = this.getBarGraphData(x.ticks());
            // Draw the Bar Graph Bars
            const barGrapher = new BarGrapher(clippedGraphArea, height);
            barGrapher.drawBars(barGraphData, x, y);

            //  __t:  The Update When Graph Brushed
            // This is the idling for double clicking
            const DOUBLE_CLICK_MAX_INTERVAL_MS = 333;
            let idleTimeout = null;
            function idled() {
                idleTimeout = null;
            }

            // Use an arrow function, so we can use 'this' inside
            const updateChart = (event) => {
                const extent = event.selection;
                let newXDomain = null;

                if (extent == null || extent[1] < extent[0] + 10) {
                    if (!idleTimeout) {
                        idleTimeout = 'not null';
                        return setTimeout(idled, DOUBLE_CLICK_MAX_INTERVAL_MS);
                    }
                    // They have doubled clicked if it gets here, so reset the domain
                    newXDomain = timeMinMax;
                } else {
                    newXDomain = [x.invert(extent[0]), x.invert(extent[1])];
                    clippedGraphArea.call(brush.clear); // Remove the shading from the brushed region
                }

                x.domain(newXDomain).nice();
                xAxis
                    .transition()
                    .duration(TRANSITION_DURATION_MS)
                    .call(
                        d3
                            .axisBottom(x)
                            .tickFormat((d) => {
                                // Format the date value using d3.timeFormat()
                                const formatter = d3.timeFormat(this.dateTimeFormat);
                                return formatter(d);
                            })
                            .tickSizeOuter(0),
                    );
                // xAxis.selectAll('text').attr('transform', 'rotate(-65,20,30)');

                y.domain(this.getYAxisValuesMinMax(...(x.domain() as [Date, Date]))).nice();
                yAxis.transition().duration(TRANSITION_DURATION_MS).call(d3.axisLeft(y));

                horizontalVisualGuides.updateLines(y);

                const barGraphData = this.getBarGraphData(x.ticks());
                barGrapher.updateBars(barGraphData, x, y);
            };

            //  __t:  Create the brush
            const brush = d3
                .brushX()
                .extent([
                    [0, 0],
                    [width, height],
                ])
                .on('end', updateChart);
            clippedGraphArea.call(brush);
        },

        getMaxTransitDataValueForTimeRange(timeMin: Date, timeMax: Date) {
            let tVal = 0;

            for (let i = 0; i < this.chartDataSorted.length - 1; i++) {
                const tdTimeInterval = [
                    this.chartDataSorted[i].time,
                    this.chartDataSorted[i + 1].time,
                ];

                if (dateIntervalsOverlap([timeMin, timeMax], tdTimeInterval)) {
                    tVal = d3.max([
                        tVal,
                        this.chartDataSorted[i].value,
                        this.chartDataSorted[i + 1].value,
                    ]);
                }
            }

            return tVal;
        },

        getYAxisValuesMinMax(timeMin: Date, timeMax: Date): [number, number] {
            return [0, this.getMaxTransitDataValueForTimeRange(timeMin, timeMax) * 1.05 + 0.000001];
        },

        getBarGraphData(ticks: Date[]): BarGraphDatum[] {
            if (ticks.length < 2) {
                return;
            }
            const ticksTimeDeltaMS = ticks[1] - ticks[0];
            const timeRangesCalc = ticks.map((tick) => [
                datePlus(tick, -ticksTimeDeltaMS / 2),
                datePlus(tick, ticksTimeDeltaMS / 2),
            ]);
            const timeRangesDraw = ticks.map((tick) => [
                datePlus(tick, -ticksTimeDeltaMS * 0.4),
                datePlus(tick, ticksTimeDeltaMS * 0.4),
            ]);
            const barYValues = timeRangesCalc.map(([timeMin, timeMax]: Date[]) =>
                this.getMaxTransitDataValueForTimeRange(timeMin, timeMax),
            );

            const barGraphData = [];
            for (let i = 0; i < barYValues.length; i++) {
                barGraphData.push({
                    timeMin: timeRangesDraw[i][0],
                    timeMax: timeRangesDraw[i][1],
                    y: barYValues[i],
                });
            }
            return barGraphData;
        },
    },
});
</script>

<style lang="scss">
@use './loc-graph.module' as *;

svg.node-occupancy-graph {
    position: relative;
    width: 100%;
    height: 100%;
}

g.bar-graph-bars-group rect {
    fill: $graph-bar-color-grey;
}

g.horizontal-guide-lines line {
    stroke: $graph-bar-color-grey;
    stroke-width: 0.5;
}

.xy-labels {
    fill: white;
    font-size: 1em;
}

.node-occupancy-graph-title {
    fill: white;
    font-size: 20px;
}
</style>
